import { isPlatformBrowser, CommonModule } from '@angular/common';
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, HostListener, Injectable, OnInit, PLATFORM_ID, Renderer2, inject, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Subject, delay } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PlayerProfileModel } from './core/models/player/player-profile-result';
import { OptionsSidebar } from './core/models/sidebar/options-sidebar.model';
import { MenuInfo } from './core/models/user/menu-info.model';
import { AuthService } from './core/services/auth/auth.service';
import { LoadingService } from './core/services/loading/loading.service';
import { LocalStorageService } from './core/services/local-storage/local-storage.service';
import { MixpanelService } from './core/services/mixpanel/mixpanel.service';
import { ModalService } from './core/services/modal/modal.service';
import { ModuleSectionsService } from './core/services/module-sections/module-sections.service';
import { ProfileService } from './core/services/profile/profile.service';
import { ScrollToTopService } from './core/services/scroll-to-top/scroll-to-top.service';
import { ClickListenerService } from './core/services/click-listener/click-listener.service';
import { LoadingComponent } from './components/user-interface/loading/loading.component';
import { MenuBarToolyComponent } from './components/user-interface/menu-bar-tooly/menu-bar-tooly.component';
import { WelcomeModalComponent } from './components/base-modal/modal-xl/welcome-modal/welcome-modal.component';
import { SteamKeyModalComponent } from './components/base-modal/steam-key-modal/steam-key-modal.component';
import { HelpModalComponent } from './components/base-modal/modal-xl/help-modal/help-modal.component';
import { BecomeGamesmithModalComponent } from './components/base-modal/become-gamesmith-modal/become-gamesmith-modal.component';
import { OpeningToolModalComponent } from './components/base-modal/opening-tool-modal/opening-tool-modal.component';
import { ReportModalComponent } from './components/base-modal/report-modal/report-modal.component';
import { PlayAdventureModalComponent } from './components/base-modal/modal-xl/play-adventure/play-adventure.component';
import { DuplicateItemModalComponent } from './components/base-modal/duplicate-item-modal/duplicate-item-modal.component';
import { UpgradeModalComponent } from './components/base-modal/upgrade-modal/upgrade-modal.component';
import { FeatureLockedModalComponent } from './components/base-modal/feature-locked-modal/feature-locked-modal.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    LoadingComponent,
    MenuBarToolyComponent,
    WelcomeModalComponent,
    SteamKeyModalComponent,
    HelpModalComponent,
    BecomeGamesmithModalComponent,
    OpeningToolModalComponent,
    ReportModalComponent,
    PlayAdventureModalComponent,
    DuplicateItemModalComponent,
    UpgradeModalComponent,
    FeatureLockedModalComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements AfterViewChecked, OnInit, AfterViewInit {
  private _loading = inject(LoadingService);
  private clickListenerService = inject(ClickListenerService);
  private localStorage = inject(LocalStorageService);
  private moduleSections = inject(ModuleSectionsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private changeDetectorRef = inject(ChangeDetectorRef);
  authService = inject(AuthService);
  private scrollToTopService = inject(ScrollToTopService);
  private renderer = inject(Renderer2);
  private profileService = inject(ProfileService);
  private mixPanelService = inject(MixpanelService);
  private modalService = inject(ModalService);
  private titleService = inject(Title);
  private metaTagService = inject(Meta);
  private document = inject<Document>(DOCUMENT);
  private platformId = inject<Object>(PLATFORM_ID);

  title = 'AuthoringHub';

  loading = false;

  menuData!: MenuInfo;

  playerProfile!: PlayerProfileModel;

  pathsWithoutMenu = [
    'auth',
    '404',
    'sharing',
    'email-verified',
    'next-fest',
    // 'learning'
  ];

  currentRoute!: string;
  isNavigationBack!: boolean;

  menuShowedLogic: boolean;

  activeOmniverseSideBar: boolean;

  optionsLeftTop: OptionsSidebar[] = [
    {
      icon: 'Home.svg',
      text: 'Home',
      name: 'home',
      section: 'home',
      link: '/creator/home',
      active: false,
      type: 'link',
    },

    {
      icon: 'Settings_Future.svg',
      text: 'My Forge Workshop',
      name: 'my_forge_workshop',
      section: 'my-omniverse',
      link: '/creator/my-omniverse',
      active: false,
      type: 'link',
    },

    {
      icon: 'Game.svg',
      text: 'Game Library',
      name: 'game_library',
      section: 'library',
      link: '/player/library',
      active: false,
      type: 'link',
    },
    {
      icon: 'Bag.svg',
      text: 'Store',
      name: 'game_store',
      section: 'store',
      link: '/creator/store',
      active: false,
      type: 'link',
    },
  ];

  optionsLeftDown: OptionsSidebar[] = [
    {
      icon: 'Book_Open.svg',
      text: 'Learn',
      name: 'learn',
      section: 'learning',
      link: '/learning',
      active: false,
      type: 'link',
    },
    {
      icon: 'Document.svg',
      text: 'News',
      name: 'news',
      section: 'news',
      link: '/news',
      active: false,
      type: 'link',
    },
    {
      icon: 'Helping.svg',
      text: 'Help',
      name: 'help',
      section: null,
      link: null,
      active: false,
      type: 'tooltip-no-active',
      topOptions: [
        {
          name: 'Discord',
          type: 'link',
          link: 'https://discord.gg/adventureforge',
        },
        {
          name: 'Instagram',
          type: 'link',
          link: 'https://www.instagram.com/adventure_forge_games',
        },
        {
          name: 'Facebook',
          type: 'link',
          link: 'https://www.facebook.com/thegameforges',
        },
        {
          name: 'Youtube',
          type: 'link',
          link: 'https://www.youtube.com/channel/UCQx09cWZFO7eo-LRAv59Z-g',
        },
      ],
      downOptions: [],
    },
  ];

  isLandingHome: boolean;
  private pageStartTime: number;
  private currentPageUrl: string;

  constructor() {
    this.scrollToTopService.checkScroll();
  }

  ngOnInit(): void {
    this.titleService.setTitle(`${environment.project_name}`);

    // this.addGoogleTagManagerScript();
    // this.addGoogleTagManagerNoscript();
  }

  ngAfterViewInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        // console.log('Navigation started');
      }
      if (event instanceof NavigationEnd) {
        this.updateActiveOption(event.url);

        // Check if the URL contains 'creator/my-omniverse'
        this.activeOmniverseSideBar = event.url.includes(
          'creator/my-omniverse',
        );

        if (event.url == '/' && this.authService.isLoggedIn()) {
          this.router.navigate(['/creator/home']);
        }

        // Track page_view event with UTM parameters and referrer
        this.mixPanelService.trackEvent('page_view', {
          url_path: event.urlAfterRedirects,
          initial_referrer: isPlatformBrowser(this.platformId) ? document.referrer : '',
          utm_source: this.getUTMParam('utm_source'),
          utm_medium: this.getUTMParam('utm_medium'),
          utm_campaign: this.getUTMParam('utm_campaign'),
        });

        // Track session start for first page view
        if (!this.pageStartTime) {
          this.mixPanelService.trackEvent('session_start', {
            landing_page: event.urlAfterRedirects,
            initial_referrer: isPlatformBrowser(this.platformId) ? document.referrer : '',
            utm_source: this.getUTMParam('utm_source'),
            utm_medium: this.getUTMParam('utm_medium'),
            utm_campaign: this.getUTMParam('utm_campaign'),
          });
        }

        // Start session duration tracking for the new page
        this.pageStartTime = Date.now();
        this.currentPageUrl = event.urlAfterRedirects;

        // console.log('Navigation ended');
      }
      if (event instanceof NavigationError) {
        console.error('Navigation error', event.error);
      }
    });
  }

  // this solves the error checked of angular forms
  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  private addGoogleTagManagerScript(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const scriptTag = this.renderer.createElement('script');
    const scriptContent = `
    (function(w,d,s,l,i){
      w[l]=w[l]||[];
      w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
      j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${environment.googleTagManagerId}');
  `;
    this.renderer.appendChild(
      scriptTag,
      this.renderer.createText(scriptContent),
    );
    const target = this.document.getElementById('scripts-analytics');
    if (target) this.renderer.appendChild(target, scriptTag);
  }

  private addGoogleTagManagerNoscript(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const noscriptTag = this.renderer.createElement('noscript');
    const iframeTag = this.renderer.createElement('iframe');
    this.renderer.setAttribute(
      iframeTag,
      'src',
      `https://www.googletagmanager.com/ns.html?id=${environment.googleTagManagerId}`,
    );
    this.renderer.setAttribute(iframeTag, 'height', '0');
    this.renderer.setAttribute(iframeTag, 'width', '0');
    this.renderer.setAttribute(
      iframeTag,
      'style',
      'display:none;visibility:hidden',
    );
    this.renderer.appendChild(noscriptTag, iframeTag);
    this.renderer.appendChild(this.document.body, noscriptTag);
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (this.document.hidden) {
      this.sendPageDurationEvent();
    } else {
      this.pageStartTime = Date.now(); // Reset start time when tab becomes visible again
    }
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.sendPageDurationEvent();
  }

  private sendPageDurationEvent() {
    if (this.pageStartTime && this.currentPageUrl) {
      const durationSeconds = (Date.now() - this.pageStartTime) / 1000;
      this.mixPanelService.trackEvent('session_duration', {
        page_url: this.currentPageUrl,
        duration_seconds: durationSeconds,
      });
      this.pageStartTime = 0; // Reset to prevent double-sending
      this.currentPageUrl = '';
    }
  }

  afterRender(): void {
    this.listenToLoading();
  }

  @HostListener('document:mousedown', ['$event'])
  documentClick(event: any): void {
    const clickedElement = event.target;
    // const isInputOrTextarea = clickedElement instanceof HTMLInputElement || clickedElement instanceof HTMLTextAreaElement;

    // if (isInputOrTextarea) {
    //   event.stopPropagation();
    // } else {
    this.clickListenerService.documentClickedTarget.next(clickedElement);
    // }
  }

  /**
   * This is to hear the back button of the browser
   * And watch if the user is on the right module
   * if he is not in the right module when the back url
   * @param event
   */

  // @HostListener('window:popstate', ['$event'])
  // onPopState(event: PopStateEvent): void {
  //   // console.log('User navigated back.');
  //   this.router.events.subscribe((event: any) => {
  //     if (event?.routerEvent instanceof NavigationEnd) {
  //       // if(!this.isNavigationBack) {
  //       // console.log("event > ", event);
  //       const moduleActive = this.localStorage.get('ModuleActive');
  //       this.isNavigationBack = true;
  //       this.currentRoute = this.router.url;
  //       let routeParts: any;
  //       if (this.currentRoute)
  //         routeParts = this.currentRoute
  //           .split('/')
  //           .filter((part) => part !== '');
  //       // console.log("routeParts > ",routeParts[0]);
  //       // console.log("moduleActive > ",moduleActive);
  //       // console.log("routeParts[0] != moduleActive > ",routeParts[0] != moduleActive);
  //       if (routeParts.length >= 2) {
  //         // the main modules have two or more parts
  //         if (routeParts[0] != moduleActive)
  //           this.router.navigate(['/' + moduleActive]).then(() => {
  //             this.isNavigationBack = false;
  //           });
  //       }
  //     }
  //     // }
  //   });
  //   // This indicates the user clicked the "Back" button

  //   // Do whatever you want to do when the user clicks "Back" here
  // }

  /**
   * Listen to the loadingSub property in the LoadingService class. This drives the
   * display of the loading spinner.
   */
  listenToLoading(): void {
    this._loading.loadingSub
      .pipe(delay(0)) // This prevents a ExpressionChangedAfterItHasBeenCheckedError for subsequent requests
      .subscribe((loading) => {
        // console.log("loading > ",loading);
        this.loading = loading;
      });
    // console.log('this.loading > ',this.loading);
  }

  /**
   * This get the actual module active
   * and if it doesn't found an active module,
   * it goes for the creator module instead
   * @returns Array of sections by module
   */
  getActiveModuleSections() {
    const activeModule = this.localStorage.get('ModuleActive');
    // console.log("this.moduleSections.getModuleSectionsByName(activeModule) > ",this.moduleSections.getModuleSectionsByName(activeModule));

    if (activeModule)
      return this.moduleSections.getModuleSectionsByName(activeModule);
    // console.log('this.moduleSections.getModuleSectionsByName("creator") > ',this.moduleSections.getModuleSectionsByName("creator"));
    return this.moduleSections.getModuleSectionsByName('creator');
  }

  /**
   * This returns the first part of the path
   * it is important to know which section is the user viewing
   * to show the menu
   * @returns string
   */
  shouldIShowMenu() {
    let show = false;
    // if(!this.menuShowedLogic) {
    const currentRoute = this.router.url; // Get the current route path
    const routeParts = currentRoute
      .split('?')[0]
      .split('/')
      .filter((part) => part !== '');
    let firstPart = '';
    if (routeParts.length) {
      firstPart = routeParts[0];
    }

    if (firstPart == '') show = true;

    if (routeParts.includes('publish-item')) return false;
    if (routeParts.includes('sea-of-legends')) return false;
    if (routeParts.includes('seaoflegends')) return false;

    if (firstPart == '404' && this.authService.isLoggedIn()) show = true;
    if (firstPart == 'sharing' && this.authService.isLoggedIn()) show = true;
    if (firstPart == 'policies') show = true;
    if (firstPart === 'email-verified') show = false;
    if (firstPart) show = !this.pathsWithoutMenu.includes(firstPart);
    if (firstPart.length > 0) this.menuShowedLogic = true;
    // }
    return show;
  }

  shouldShowClosedBeta() {
    if (!this.authService.isLoggedIn()) return false;

    const currentRoute = this.router.url; // Get the current route path
    const routeParts = currentRoute
      .split('?')[0]
      .split('/')
      .filter((part) => part !== '');
    let firstPart = '';

    if (routeParts.length) {
      firstPart = routeParts[0];
    }

    if (firstPart == 'home') return false;
    if (firstPart == '') return false;
    if (firstPart == 'next-fest') return false;
    if (firstPart == 'policies') return false;
    if (firstPart == 'terms-of-service') return false;
    if (firstPart == 'news') return false;
    if (firstPart == 'game-jam-2024-col') return false;

    return true;
  }

  async getProfileData() {
    // this.playerProfile = JSON.parse(this.localStorage.get('PlayerProfile'))
    this.menuData = {
      points: 12323,
      pointsName: 'Neo',
      userInitials: 'RE',
      userName: '',
    };
    // console.log("playerProfile > ",this.playerProfile);

    // console.log("menuData > ",this.menuData);
  }

  openWelcomeModal() {
    this.modalService.openWelcomeModal();
  }

  updateActiveOption(url: string) {
    [...this.optionsLeftTop, ...this.optionsLeftDown].forEach((option) => {
      option.active = false;

      if (url.includes(option.link)) {
        option.active = true;
        return;
      }
    });
  }

  /**
   * Get UTM parameter value from current URL
   * @param paramName UTM parameter name (e.g., 'utm_source', 'utm_medium', 'utm_campaign')
   * @returns Parameter value or null if not found
   */
  private getUTMParam(paramName: string): string | null {
    try {
      // Check if we're in the browser environment (not SSR)
      if (!isPlatformBrowser(this.platformId)) {
        return null;
      }
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(paramName);
    } catch (error) {
      console.error('Error getting UTM parameter:', error);
      return null;
    }
  }
}
